import { addError, addSuccess } from '@/store/reducers/ToastStore';
import { FeedbackButtons } from '@/components/chat/FeedbackButtons';
import { getChatAPI } from '@/api/ChatApi';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { getThreadById, updateThread } from '@/store/reducers/ChatStore';
import { Reaction } from '@/models/Reaction';
import { THREAD_FEEDBACK_PREFIX } from '@/analytics/props/feedback';
import { ThreadId } from '@/models/Thread';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

type ThreadFeedbackButtonsProps = {
  threadId: ThreadId;
};

export const ThreadFeedbackButtons = ({
  threadId,
}: ThreadFeedbackButtonsProps) => {
  const thread = useAppSelector((state) => getThreadById(state, threadId));
  const dispatch = useAppDispatch();
  const chatApi = getChatAPI();

  // Can't give feedback without a thread
  if (!thread) {
    getLayoverLogger().logError(
      `threadId not found in ChatStore [threadId=${threadId}]`,
    );
    return null;
  }

  const onUpdateReactionState = async (newReaction: Reaction | null) => {
    const oldThread = thread;
    const updatedThread = { ...thread, viewerReaction: newReaction };

    // Show new state immediately, assume request will succeed
    dispatch(updateThread({ thread: updatedThread }));

    try {
      // Save state to server
      await chatApi.saveThreadReaction({
        threadId: updatedThread.id,
        reaction: updatedThread.viewerReaction,
      });
    } catch (e: any) {
      getLayoverLogger().logError('Failed to update thread feedback state:', e);
      // roll back thumbs up/down in UI
      dispatch(updateThread({ thread: oldThread }));
      dispatch(addError({ text: 'Error saving feedback. Please try again.' }));
    }
  };

  const onSubmitFeedback = async (feedback: string) => {
    try {
      await chatApi.saveThreadFeedback({
        threadId: thread.id,
        feedback,
      });
      dispatch(addSuccess({ text: 'Thanks for your feedback!' }));
    } catch (e: any) {
      getLayoverLogger().logError('Error saving thread feedback:', e);
      dispatch(
        addError({
          text: 'Error submitting feedback. Please try again.',
        }),
      );
    }
  };

  return (
    <FeedbackButtons
      viewerReaction={thread.viewerReaction}
      onUpdateReactionState={onUpdateReactionState}
      onSubmitFeedback={onSubmitFeedback}
      heapPrefix={THREAD_FEEDBACK_PREFIX}
      heapProps={{ threadId }}
    />
  );
};
